import { useCallback, useRef, useState } from "react";
import S3Uploader from "../../utils/s3Uploader";

/**
 * Drives a browser-direct S3 multipart upload and reports enough about it to
 * show an honest progress panel: parts done, bytes moved, a smoothed transfer
 * rate, an ETA, and whether this run resumed an earlier interrupted one.
 *
 * Bytes and rate are derived from PART completions (S3 acknowledges a part,
 * not a byte), so they advance in steps of one part rather than smoothly.
 * That's why the rate is smoothed and the ETA is deliberately coarse — a
 * per-second countdown here would be invented precision.
 */

const IDLE = {
  phase: "idle", // idle | preparing | uploading | finalizing | done | error | cancelled
  percent: 0,
  uploadedParts: 0,
  totalParts: 0,
  bytesSent: 0,
  totalBytes: 0,
  bytesPerSecond: 0,
  etaSeconds: null,
  resumed: false,
  error: null,
};

// Exponential moving average — one slow part on a shared line shouldn't make
// the displayed rate collapse, and one fast part shouldn't make it spike.
const RATE_SMOOTHING = 0.35;

export default function useS3Upload() {
  const [state, setState] = useState(IDLE);
  const uploaderRef = useRef(null);
  const rateRef = useRef({ lastAt: 0, lastBytes: 0, ema: 0 });

  const reset = useCallback(() => {
    uploaderRef.current = null;
    rateRef.current = { lastAt: 0, lastBytes: 0, ema: 0 };
    setState(IDLE);
  }, []);

  const cancel = useCallback(async () => {
    const uploader = uploaderRef.current;
    if (!uploader) return;
    setState((s) => ({ ...s, phase: "cancelled" }));
    // Tells S3 to discard the parts already stored, so a cancelled multi-GB
    // upload doesn't keep costing storage until the lifecycle rule catches it.
    await uploader.abort();
  }, []);

  /**
   * Upload `file` and resolve with its staged S3 key. Throws on failure so
   * the caller can keep its own error handling (a toast, usually).
   */
  const start = useCallback(async (file, kind = "video") => {
    const uploader = new S3Uploader(file, kind);
    uploaderRef.current = uploader;
    rateRef.current = { lastAt: Date.now(), lastBytes: 0, ema: 0 };

    setState({ ...IDLE, phase: "preparing", totalBytes: file.size });

    uploader.onProgress = ({ uploadedParts, totalParts, progress }) => {
      const bytesSent = Math.min(file.size, Math.round((progress / 100) * file.size));
      const now = Date.now();
      const { lastAt, lastBytes, ema } = rateRef.current;
      const elapsed = (now - lastAt) / 1000;

      let nextEma = ema;
      if (elapsed > 0.25 && bytesSent > lastBytes) {
        const instant = (bytesSent - lastBytes) / elapsed;
        nextEma = ema ? ema * (1 - RATE_SMOOTHING) + instant * RATE_SMOOTHING : instant;
        rateRef.current = { lastAt: now, lastBytes: bytesSent, ema: nextEma };
      }

      const remaining = file.size - bytesSent;
      setState((s) => ({
        ...s,
        phase: "uploading",
        percent: progress,
        uploadedParts,
        totalParts,
        bytesSent,
        totalBytes: file.size,
        bytesPerSecond: nextEma,
        etaSeconds: nextEma > 0 && remaining > 0 ? remaining / nextEma : null,
      }));
    };

    try {
      const resumed = await uploader.initialize();
      if (resumed) setState((s) => ({ ...s, resumed: true }));

      if (uploader.alreadyComplete) {
        // This exact file finished uploading in an earlier session but was
        // never submitted. The object is already staged on S3, so there is
        // nothing to send — go straight to done.
        setState((s) => ({ ...s, phase: "done", percent: 100, bytesSent: file.size, etaSeconds: null }));
        return uploader.stagedKey;
      }

      await uploader.uploadParts();
      setState((s) => ({ ...s, phase: "finalizing", percent: 100 }));

      const result = await uploader.complete();
      setState((s) => ({ ...s, phase: "done", percent: 100, etaSeconds: null }));
      return result.staged_key;
    } catch (err) {
      if (uploader.aborted) {
        setState((s) => ({ ...s, phase: "cancelled" }));
        throw new Error("Upload cancelled");
      }
      setState((s) => ({ ...s, phase: "error", error: err.message || "Upload failed" }));
      throw err;
    }
  }, []);

  return { state, start, cancel, reset, isBusy: ["preparing", "uploading", "finalizing"].includes(state.phase) };
}
